import { zodResolver } from '@hookform/resolvers/zod'
import { Avatar, Flex, Message, Text, Tooltip } from '@workwhile/ui'
import { TipAmountInput } from 'components/SendTipModal/components/TipAmountInput'
import { formatCurrency } from 'lib/string'
import { Info } from 'lucide-react'
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTheme } from 'styled-components'
import { BulkTipVariant } from '../constants'
import { tipsSchema } from '../schema'
import { useTipHistory } from 'queries/worker'

type TipHistoryTooltipProps = {
  paymentHistory: { userPaymentId: number; amount: number }[]
  totalPaymentAmount: number
}

const TipHistoryTooltip = ({
  paymentHistory,
  totalPaymentAmount,
}: TipHistoryTooltipProps) => {
  const theme = useTheme()

  return (
    <Tooltip
      content={
        <Flex flexDirection="column" gap={1}>
          <Text fontWeight="bold">Tip History:</Text>
          {paymentHistory.map(({ userPaymentId, amount }) => (
            <Text key={userPaymentId}>
              {formatCurrency(amount)} Tipped to worker
            </Text>
          ))}
        </Flex>
      }
      variant="neutral"
    >
      <Flex gap={1} alignItems="center">
        <Text style={{ whiteSpace: 'nowrap' }}>
          Tip: {formatCurrency(totalPaymentAmount)}
        </Text>
        <Info size={16} color={theme.colors.primary} />
      </Flex>
    </Tooltip>
  )
}

type WorkerRowProps = {
  index: number
  workerName?: string
  profilePicUrl?: string
  paymentHistory: { userPaymentId: number; amount: number }[]
  totalPaymentAmount: number
}

const WorkerRow = ({
  index,
  workerName,
  profilePicUrl,
  paymentHistory,
  totalPaymentAmount,
}: WorkerRowProps) => {
  return (
    <Flex justifyContent="space-between" gap={2}>
      <Flex alignItems="center" gap={2}>
        <Avatar
          size="large"
          user={{
            name: workerName,
            photoUrl: profilePicUrl,
          }}
        />

        <Text>{workerName}</Text>
      </Flex>

      <Flex alignItems="center" gap={2}>
        {paymentHistory.length > 0 && (
          <TipHistoryTooltip
            paymentHistory={paymentHistory}
            totalPaymentAmount={totalPaymentAmount}
          />
        )}

        <TipAmountInput title="" name={`tips.${index}.amount`} width="120px" />
      </Flex>
    </Flex>
  )
}

type BulkTipModalVariantCustomProps = {
  shiftId: number
  onSubmit: (data: { tips: { workId: number; amount: number }[] }) => void
}

export const BulkTipModalVariantCustom = ({
  shiftId,
  onSubmit,
}: BulkTipModalVariantCustomProps) => {
  const { data: works = [] } = useTipHistory(
    { shiftId },
    {
      enabled: Boolean(shiftId),
      select: (data) =>
        data.workHistory.map((item) => ({
          workId: item.work.workId,
          workerName: item.work.worker.name,
          totalPaymentAmount: item.totalPaymentAmount,
          profilePicUrl: item.work.worker.profilePicUrl,
          paymentHistory: item.paymentHistory,
        })),
    }
  )
  const form = useForm({
    defaultValues: {
      tips: works.map((work) => ({
        workId: work.workId,
        amount: null,
      })),
    },
    resolver: zodResolver(tipsSchema),
  })

  const { fields } = useFieldArray({
    control: form.control,
    name: 'tips',
  })
  const tips = useWatch({ name: 'tips', control: form.control }) ?? []
  const totalAmount = tips.reduce(
    (acc, { amount }) => acc + (Number(amount) || 0),
    0
  )
  const tipsError =
    form.formState.errors.tips?.message ??
    form.formState.errors.tips?.root?.message

  const handleSubmit = ({ tips }) => {
    onSubmit({ tips: tips.filter((tip) => tip.amount && tip.amount > 0) })
  }

  return (
    <FormProvider {...form}>
      <form
        id={BulkTipVariant.Custom}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <Flex flexDirection="column" gap={4}>
          <Text>Each worker will receive the amount you set for them.</Text>

          <Flex flexDirection="column" gap={1}>
            <Flex justifyContent="space-between">
              <Text fontWeight={'bold'}>Worker</Text>
              <Text textAlign="right" fontWeight={'bold'}>
                Amount
              </Text>
            </Flex>

            <Flex flexDirection="column" gap={2}>
              {fields.map((field, index) => {
                const work = works[index]
                const paymentHistory = work.paymentHistory.filter(
                  (
                    item
                  ): item is {
                    userPaymentId: number
                    amount: number
                    createdAt: string
                  } => 'userPaymentId' in item
                )
                return (
                  <WorkerRow
                    key={field.id}
                    {...work}
                    paymentHistory={paymentHistory}
                    index={index}
                  />
                )
              })}
            </Flex>
          </Flex>

          <Flex justifyContent="flex-end" gap={2}>
            <Text>Total amount:</Text>
            <Text textAlign="right" fontWeight="bold">
              {formatCurrency(totalAmount)}
            </Text>
          </Flex>

          {tipsError ? <Message variant="error">{tipsError}</Message> : null}
        </Flex>
      </form>
    </FormProvider>
  )
}
